<template>
  <div
    class="fts-history-lite"
    :data-history-load="loading ? 'loading' : 'loaded'"
    v-if="show"
  >
    <!-- history header -->
    <div class="history-header">
      <div class="history-header__l">
        <div class="history-header-title">
          <template v-if="mode === 'history'">
            History
          </template>

          <template v-else>
            Comment
          </template>
        </div>

        <div class="history-header-count" v-if="getCount && !loading">
          {{getCount}}
        </div>
      </div>

      <div class="history-header__r">
        <fts-dropdown-select
          append-content-class="dropdown-menu dropdown-menu-w-xs dropdown-menu--lite" 
        >
          <div 
            slot="toggle" 
            slot-scope="{ isShow }" 
            class="history-header-btn" 
            :class="{'history-header-btn--active': isShow}"
          >
            <span class="material-icons-outlined">import_export</span>
          </div>

          <template #menu="{ select }">
            <div 
              class="dropdown-option dropdown-option--lite" 
              @click="onSort(select, 'DESC')" 
            >
              <div class="dropdown-option-icon">
                <span class="material-icons-outlined">arrow_downward</span>
              </div>
              <div>Newest first</div>
            </div>

            <div 
              class="dropdown-option dropdown-option--lite" 
              @click="onSort(select, 'ASC')" 
            >
              <div class="dropdown-option-icon">
                <span class="material-icons-outlined">arrow_upward</span>
              </div>
              <div>Oldest first</div>
            </div>
          </template>
        </fts-dropdown-select>

        <div class="history-header-btn" @click="onClose">
          <span class="material-icons-outlined">close</span>
        </div>
      </div>
    </div>

    <!-- history section -->
    <div
      class="history-section fts-scrollbar fts-scrollbar-overlay"
      ref="historySection"
    >
      <div key="load-archive" class="fts-text-center" v-if="historyArchive && !historyLoadMore">
        <fts-btn
          text
          text-transform-init
          :loading="loading"
          class="history-load-btn fts-font-normal"
          @click="loadArchive"
        >
          Older history is archived, click to show
        </fts-btn>
      </div>

      <fts-spinner height="100%" v-if="initLoading" />

      <div class="history-section-intro" key="initial-notification" v-else-if="!locusId && mode == 'notification'">
        <font-awesome-icon :icon="['far', 'bell']" class="history-section-intro-icon" />
        <div class="history-section-intro-title">
          Notification status tracking
        </div>
        <div class="history-section-intro-description">
          Stay informed with notification status tracking for timely updates.
        </div>
      </div>
      
      <div class="history-section-intro" key="empty-notification" v-else-if="mode == 'notification'">
        <font-awesome-icon :icon="['far', 'bell']" class="history-section-intro-icon" />
        <div class="history-section-intro-title">
          Notification status tracking
        </div>
        <div class="history-section-intro-description">
          Stay informed with notification status tracking for timely updates.
        </div>
      </div>

      <template v-else-if="(histories && histories.length)">
        <div key="load-more" class="fts-text-center" v-if="historyLoadMore">
          <fts-btn
            text
            text-transform-init
            :loading="loading"
            class="history-load-btn fts-font-normal"
            @click="nextPage"
          >
            more histories
          </fts-btn>
        </div>

        <div
          v-for="(history, historyIndex) in histories" 
          :key="history.id"
        >
          <fts-history-content 
            :data.sync="histories[historyIndex]" 
            :vector-id="vectorId" 
            :locus-id="locusId" 
            :states="states" 
            :users="users" 
            :comment-section="commentSection" 
            :mode="mode" 
            :is-last="historyIndex == histories.length - 1" 
            :is-lite="true" 
            :data-index="historyIndex" 
            @comment:reply="replyComment"
          ></fts-history-content>

          <template
            v-if="history.historyItems && history.historyItems.length"
          >
            <fts-history-content 
              v-for="(item, itemIndex) in history.historyItems" 
              :key="item.id" 
              :data.sync="history.historyItems[itemIndex]" 
              :vector-id="vectorId" 
              :locus-id="locusId" 
              :states="states" 
              :users="users" 
              :comment-section="commentSection" 
              :mode="mode" 
              :child="true" 
              :is-lite="true" 
              @comment:reply="replyComment"
            ></fts-history-content>
          </template>
        </div>
      </template>

      <div class="history-section-intro" key="empty-history" v-else-if="mode == 'history'">
        <div class="history-section-intro-state">
          <img :src="emptyHistoryImage" alt="empty-history-image">
        </div>

        <div class="history-section-intro-title">
          No activity recorded yet
        </div>

        <div class="history-section-intro-description">
          Track changes, view updates, and stay informed on how this record evolves over time.
        </div>
      </div>

      <div class="history-section-intro" key="empty-comment" v-else>
        <div class="history-section-intro-state">
          <img :src="emptyCommentImage" alt="empty-comment-image">
        </div>

        <div class="history-section-intro-title">
          Start a conversation
        </div>

        <div class="history-section-intro-description">
          Share thoughts, react to comments, and collaboration with your team.
        </div>
      </div>
    </div>
    <!-- history footer -->
    <div class="history-footer" v-if="commentSection && locusId && mode !== 'notification'">
      <fts-comment 
        :vector-id="vectorId" 
        :locus-id="locusId" 
        :initial-comment="true" 
        :avatar="false" 
        :send-action="true" 
        v-model="collaboration" 
        placeholder="Add a comment..." 
        @added="addHistory($event)" 
        @submitting="updateHistorySubmitQueue" 
        ref="historySectionComment"
      ></fts-comment>
    </div>
  </div>
</template>

<script>
import { classificationStore } from '../../store/classificationStore';
import { emptyCommentImage, emptyHistoryImage } from '../../commons/images';

import FTSBtn from '../../elements/FTSBtn';
import FTSComment from '../../elements/FTSComment';
import FTSDropdownSelect from '../../elements/FTSDropdownSelect';
import FTSHistoryContent from '../../elements/history/FTSHistoryContent';
import FTSSpinner from '../../elements/FTSSpinner';

const MESSAGE = Object.freeze({
  HISTORY_SUBMIT_QUEUE: "Please wait for the comment to be submitted."
});

const MODE = Object.freeze({
  COMMENT: "comment",
  HISTORY: "history"
});

export default {
  props: {
    locusId: [String, Number],
    vectorId: [String, Number],
    states: Array,
    users: Array,
    commentSection: {
      type: Boolean,
      default: true,
    },
    enableNotification: {
      type: Boolean,
      default: true
    },
    initOpen: Boolean
  },
  components: {
    "fts-btn": FTSBtn,
    "fts-comment": FTSComment,
    "fts-dropdown-select": FTSDropdownSelect,
    "fts-history-content": FTSHistoryContent,
    "fts-spinner": FTSSpinner
  },
  data: () => ({
    show: false,
    initLoading: false,
    loading: false,
    mode: MODE.COMMENT,
    histories: [],
    historyCurrentPage: 0,
    historyLoadMore: false,
    historyArchive: false,
    // Sticky once the reader opens the archived tail: it pages on its own counter and every
    // later page has to keep asking for it (SS05770).
    historyWithArchive: false,
    notification: [],
    collaboration: { attachments: [] },
    historySubmitQueue: [],
    sort: "ASC",
    emptyCommentImage,
    emptyHistoryImage,
  }),
  methods: {
    resetScroll() {
      this.$refs.historySection && this.$refs.historySection.scrollTo(0, 0);
    },    
    onClose() {
      this.show = false;
    },
    onSort(callback, type) {
      if (typeof callback === "function") {
        callback();
      }

      if (this.sort === type) {
        return;
      }

      if (type === "DESC") {
        this.histories = _.orderBy(this.histories, history => new Date(history.on), "desc");
        this.sort = "DESC";
      } else {
        this.histories = _.orderBy(this.histories, history => new Date(history.on));
        this.sort = "ASC";
      }
    },
    replyComment(content) {
      if (this.historySubmitQueue.length) {
        this.$toasted.info(MESSAGE.HISTORY_SUBMIT_QUEUE);
      } else {
        Boolean(content) && this.$set(this.collaboration, "content", content);
        this.focusSectionComment();
      }
    },
    updateHistorySubmitQueue(item) {
      if (item.isSubmitting) {
        this.historySubmitQueue.push(item.id);
      } else {
        const idx = this.historySubmitQueue.indexOf(item.id);
        idx > -1 && this.historySubmitQueue.splice(idx, 1);
      }
    },
    addHistory(item) {
      this.histories.push(item);
      this.$emit("added");
      this.$refs.historySection && this.$nextTick(() => {
        this.$refs.historySection.scrollTop = this.$refs.historySection.scrollHeight;
      });
    },
    // P2 live propagation (FETIAS docs/collaboration-comment-edit-delete-proposal.md §9) — same
    // behaviour as elements/history/FTSHistory.vue: silent first-page refetch when the server's
    // content-free `commentChanged` frame names this entry and came from someone else.
    _onCommentChanged(payload) {
      if (!payload || !this.show || !this.locusId) return;
      if (String(payload.entryId) !== String(this.locusId)) return;
      if (payload.by && payload.by === classificationStore.state.currentUsername) return;
      if (this.historySubmitQueue.length) return;

      clearTimeout(this._commentChangedTimer);
      this._commentChangedTimer = setTimeout(() => this.refreshFromPush(), 400);
    },
    async refreshFromPush() {
      if (!this.show || this.mode === 'notification' || this.historyCurrentPage !== 0) return;
      // The archived tail is a manual, merged read — a silent page-0 refetch would drop it.
      if (this.historyWithArchive) return;
      const historyMode = this.mode;

      try {
        const res = await classificationStore.dispatch("retrieveHistory", {
          moduleId: this.vectorId,
          id: this.locusId,
          history: historyMode == MODE.HISTORY,
          currentPage: 0
        });

        if (historyMode != this.mode || this.historyCurrentPage !== 0) return;

        this.histories = res.data.histories;
        this.historyLoadMore = Boolean(res.data.hasMore);
      } catch (err) {
        console.error(err);
      }
    },
    async retrieveHistory(loadMore, withArchive) {
      if (this.locusId && this.show) {
        const historyMode = this.mode;
        this.loading = true;
        // Opening the archive continues the timeline instead of restarting it: `a=true` returns
        // archived rows only, paged on their own counter, and the first archived page is merged
        // into the live rows already on screen.
        const enteringArchive = Boolean(withArchive) && !this.historyWithArchive;
        if (enteringArchive) {
          this.historyWithArchive = true;
          this.historyCurrentPage = 0;
        } else if (loadMore) {
          this.historyCurrentPage += 1;
        } else {
          this.historyWithArchive = false;
          this.resetScroll();
          this.historyCurrentPage = 0;
        }

        try {
          const res = await classificationStore.dispatch("retrieveHistory", {
            moduleId: this.vectorId,
            id: this.locusId,
            history: historyMode == MODE.HISTORY,
            currentPage: this.historyCurrentPage,
            archive: this.historyWithArchive
          });

          if (historyMode != this.mode) return;

          if (loadMore || enteringArchive) {
            const firstEl = document.querySelector('[data-index="0"]')
            const firstElOffsetTop = firstEl ? firstEl.offsetTop : 0;
            this.histories = _.sortBy([
              ...res.data.histories.filter((x) => !this.histories.some((y) => y.id === x.id)),
              ...this.histories
            ], 'on');

            this.$nextTick(() => {
              if (firstEl) this.$refs.historySection.scrollTo(0, firstEl.offsetTop - firstElOffsetTop)
            });
          } else {
            this.histories = res.data.histories;
          }

          this.historyLoadMore = Boolean(res.data.hasMore);
          this.historyArchive = Boolean(res.data.hasArchive);
        } catch (err) {
          console.error(err);
        } finally {
          this.loading = false;
        }
      }
    },
    async retrieveNotification() {
      if (this.locusId && this.show) {
        this.loading = true;
        this.resetScroll();

        try {
          let res = await classificationStore.dispatch("retrieveNotification", {
            moduleId: this.vectorId,
            id: this.locusId,
          });
          this.notification = res.data.notifications;
        } catch (err) {
          console.error(err);
        } finally {
          this.loading = false;
        }
      }
    },
    nextPage() {
      if (!this.loading) {
        this.retrieveHistory(true);
      }
    },
    loadArchive() {
      if (!this.loading) {
        this.retrieveHistory(false, true);
      }
    },
    async toggleHistory(mode) {
      const currentMode = this.mode;
      const isShow = this.show;

      if ((mode === currentMode || !mode) && isShow) {
        this.show = false;
      } else if (currentMode !== mode && isShow) {
        
        if (mode) {
          this.mode = mode;
        }

        this.show = true;
        this.initLoading = true;
        await this.retrieveHistory();
        this.initLoading = false;
      } else if (!isShow) {

        if (mode) {
          this.mode = mode;
        }

        this.show = true;
        this.initLoading = true;
        await this.retrieveHistory();
        this.initLoading = false;
      }
    },
    async toggleMode(mode) {
      if (this.historySubmitQueue.length) {
        this.$toasted.info(MESSAGE.HISTORY_SUBMIT_QUEUE);
      } else if (this.mode !== mode) {
        this.initLoading = true;
        this.mode = mode;
        this.collaboration = { attachments: [] };
        if (this.mode === "notification") {
          await this.retrieveNotification();
        } else {
          await this.retrieveHistory();
        }
        this.initLoading = false;
      }
    },
    focusSectionComment() {
      if (!this.$refs.historySectionComment.showComment) {
        this.$refs.historySectionComment.showComment = true;
      }
      this.$refs.historySectionComment.onFocus();
    }
  },
  computed: {
    getCount() {
      return Array.isArray(this.histories) ? this.histories.length : 0;
    }
  },
  watch: {
    show(val) {
      if (val) {
        this.$emit("mode:change", this.mode);
      } else {
        this.$emit("mode:change", null);
      }
    },
    mode(val) {
      this.sort = "ASC";

      if (this.show) {
        this.$emit("mode:change", val);
      }
    }
  },
  mounted() {
    if (!this.commentSection) {
      this.mode = MODE.HISTORY;
    }

    if (this.initOpen) {
      this.$nextTick(this.toggleHistory);
    }

    if (window.eventBus) {
      window.eventBus.$on("signalr.commentChanged", this._onCommentChanged);
    }
  },
  beforeDestroy() {
    clearTimeout(this._commentChangedTimer);
    if (window.eventBus) {
      window.eventBus.$off("signalr.commentChanged", this._onCommentChanged);
    }
  }
};
</script>

<style lang="scss">
@use "./src/assets/scss/vars" as *;

.fts-history-lite {
  $width: 470px !default;
  flex: 1 1 $width;
  max-width: $width;
  min-width: $width;
  display: flex;
  flex-direction: column;
  border-radius: var(--inistate-rounded-8);
  box-shadow: 0px 0px 5px 0px rgba(0, 0, 0, 0.1);
  background-color: var(--inistate-color-shade-white);

  .history-header {
    display: flex;
    align-items: center;
    padding: 16px;
    border-bottom: 1px solid var(--inistate-color-neutral-100);

    .history-header__l {
      flex: 1;
      min-height: 36px;
      display: flex;
      align-items: center;
    }

    .history-header__r {
      flex-shrink: 0;
      display: flex;
      align-items: center;
      margin-left: 16px;
    }

    .history-header-title {
      color: var(--inistate-text-secondary);
      font-weight: var(--inistate-font-weight-medium);
      font-size: var(--inistate-paragraph-text-16);
      line-height: var(--inistate-paragraph-text-16--line-height);
    }

    .history-header-count {
      height: 20px;
      min-width: 20px;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-left: 8px;
      padding-left: 6px;
      padding-right: 6px;
      border-radius: 10em;
      background-color: var(--inistate-badge-light-grey);
      color: var(--inistate-text-secondary);
      font-weight: var(--inistate-font-weight-medium);
      font-size: var(--inistate-paragraph-text-12);
      line-height: var(--inistate-paragraph-text-12--line-height);
    }

    .history-header-btn {
      height: 36px;
      width: 36px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: var(--inistate-rounded-4);
      background-color: transparent;
      color: var(--inistate-icon-secondary);
      transition: background-color 0.3s ease, color 0.3s ease;
      @include user-select-none;

      > span {
        font-size: 20px;
      }

      &:hover {
        background-color: var(--inistate-background-secondary);
        color: var(--inistate-icon-primary);
        cursor: pointer;
      }

      &--active {
        background-color: var(--inistate-background-secondary);
        color: var(--inistate-icon-primary);
      }
    }
  }

  .history-section {
    flex: 1;
    position: relative;
    padding-bottom: 0.5rem;
  }

  .history-section-intro {
    height: 100%;
    @include flex-center-middle;
    flex-direction: column;
    padding: 3rem 2rem;
    text-align: center;
    font-size: var(--inistate-label-text-14);
    line-height: var(--inistate-paragraph-text-14--line-height);
  }

  .history-section-intro-icon {
    margin-bottom: 0.75rem;
    font-size: 1.5rem;
    color: var(--fts-default);
  }

  .history-section-intro-state {
    max-width: 100%;
    width: 190px;
    margin-bottom: 12px;
    > img {
      display: flex;
      align-items: center;
      justify-content: center;
      width: auto;
      height: auto;
      max-width: 100%;
      max-height: 100%;
      margin: auto;
    }
  }

  .history-section-intro-title {
    margin-bottom: 6px;
    font-weight: var(--inistate-font-weight-medium);
    font-size: var(--inistate-paragraph-text-16);
    line-height: var(--inistate-paragraph-text-16--line-height);
    color: var(--inistate-text-primary);
  }

  .history-section-intro-description {
    max-width: 315px;
    font-size: var(--inistate-label-text-14);
    line-height: var(--inistate-label-text-14--line-height);
    color: var(--inistate-text-secondary);
  }

  .history-footer {
    padding-top: 0.5rem;
    padding-bottom: 0.5rem;
    border-top: 1px solid var(--fts-border);

    .fts-comment .comment-content {
      border: none;
    }
  }

  .history-load-btn {
    margin-top: 0.5rem;
    margin-bottom: 0.5rem;
  }
}
</style>